<template>
  <div v-if="shape.canUseWidgetCondition" class="pb-6 relative mt-2.5 w-full">
    <!-- 已添加动效：显示条件编辑 -->
    <template v-if="hasConditions">
      <AttrGroup title="条件切换" :card="true" :visible="true">
        <div class="text-xs opacity-50 px-2.5 mb-2">
          按条件显示对应的子组，最后一个子组作为默认（else）
        </div>
        <div class="flex flex-col gap-2.5 px-2.5">
          <div
            v-for="(item, index) in conditionItems"
            :key="item.widget.id"
            class="bg-[#2E2E2E] rounded-lg p-2.5 px-0"
          >
            <!-- 条件标签 -->
            <div class="text-xs opacity-70 mb-1.5">
              <span v-if="index < conditionItems.length - 1">
                条件{{ index + 1 }}：显示「{{ item.widget.attrs.name }}」
              </span>
              <span v-else>
                默认（else）：显示「{{ item.widget.attrs.name }}」
              </span>
            </div>

            <!-- 条件编辑区（非最后一个），复用 ConditionWraper -->
            <template v-if="index < conditionItems.length - 1 && item.condition">
              <AttrGroup
                title="触发方式"
                :show-add="getAddEnable(index)"
                :card="true"
                :visible="!!item.condition.children.length"
                @add="() => onAddCompare(index)"
              >
                <div class="py-1">
                  <ConditionWraper
                    :condition="item.condition"
                    :disabled="!!attrDisabled"
                    :addable="getAddEnable(index)"
                    @update="(id: string, data: any) => onUpdate(index, id, data)"
                    @add="(compareId: string) => onAddParam(index, compareId)"
                    @delete="(paramId: string) => onDelete(index, paramId)"
                  />
                </div>
              </AttrGroup>
            </template>
          </div>
        </div>
      </AttrGroup>

      <!-- 动画过程 -->
      <AttrGroup title="动画过程" :show-add="true" :card="false" @add="onAddAnimation">
        <div class="gap-1 flex flex-col">
          <IFTTTAnimationItem
            v-for="ani in sortedAnimations"
            :key="ani.id"
            :ifttt="virtualIfttt"
            :animation="ani"
            class="bg-black-card"
            @delete="() => onDelAnimation(ani.id)"
            @update="(id: string, data: any) => onUpdateAnimation(id, data)"
          />
        </div>
      </AttrGroup>
    </template>

    <!-- 未添加动效：显示添加按钮 -->
    <a-button
      v-else
      class="!w-full"
      type="text"
      @click="onAddConditions"
    >添加动效</a-button>
  </div>
</template>

<script setup lang="ts">
import { Shape } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { GlobalConfig } from '@/services/gloabl.config';
import { computed, PropType, watch } from 'vue';
import generateID from '@/utils/generate-id';
import { Modal } from 'ant-design-vue';
import { cloneDeep } from 'lodash-es';
import ConditionWraper from '../Condition/ConditionWraper.vue';
import IFTTTAnimationItem from '../IFTTTAnimationItem.vue';
import { useIfttt } from '@/hooks/use-ifttt';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);

// 子 Widget 列表（反转以匹配左侧结构树的显示顺序）
const childWidgets = computed(() => [...props.shape.children].reverse());

// 条件数据存储在父 Widget 的 attrs.widgetConditions 中
const conditions = computed<Condition[]>({
  get() {
    return props.shape.attrs.widgetConditions ?? [];
  },
  set(val) {
    props.shape.setAttrs({ widgetConditions: val });
  }
});

// 是否已添加动效条件
const hasConditions = computed(() => conditions.value.length > 0);

// 子元素数量变化时，自动补齐/截断条件（仅在已有条件时）
watch(
  childWidgets,
  (widgets) => {
    if (!hasConditions.value) return;
    const targetCount = Math.max(widgets.length - 1, 0);
    const current = [...(conditions.value ?? [])];
    if (current.length === targetCount) return;

    while (current.length < targetCount) {
      current.push(createEmptyCondition());
    }
    if (current.length > targetCount) {
      current.length = targetCount;
    }
    conditions.value = current;

    // 子元素不足2个时，彻底清除动效数据
    if (targetCount === 0) {
      props.shape.setAttrs({ widgetConditions: undefined, widgetAnimations: undefined });
      return;
    }
  },
  { immediate: true }
);

interface ConditionItem {
  widget: Shape;
  condition?: Condition;
}

const conditionItems = computed<ConditionItem[]>(() => {
  return childWidgets.value.map((widget, index) => ({
    widget,
    condition: index < conditions.value.length ? conditions.value[index] : undefined
  }));
});

function createEmptyCondition(): Condition {
  return {
    id: generateID(),
    logicalOperator: 'and',
    children: [
      {
        id: generateID(),
        flag: 'compare',
        type: 'when',
        logicalOperator: 'and',
        children: [
          {
            id: generateID(),
            flag: 'compare-param',
            whenSourceKey: 'timeHour',
            whenOperator: 'MoreThanOrEqual',
            whenValue: 9
          }
        ]
      }
    ]
  };
}

/** 点击"添加动效"按钮 */
const onAddConditions = () => {
  const targetCount = Math.max(childWidgets.value.length - 1, 0);
  if (targetCount <= 0) return;
  const list: Condition[] = [];
  for (let i = 0; i < targetCount; i++) {
    list.push(createEmptyCondition());
  }
  conditions.value = list;
  // 同时创建默认动画
  animations.value = [createDefaultAnimation()];
};

/** 点击"删除动效"按钮 */
const onRemoveConditions = () => {
  Modal.confirm({
    title: '确认删除',
    content: '删除后所有子组的条件切换配置将被清空',
    okText: '确认',
    cancelText: '取消',
    onOk: () => {
      conditions.value = [];
      animations.value = [];
    }
  });
};

/** 获取某个条件的参数总数 */
const getConditionParamCount = (conditionIndex: number) => {
  const condition = conditions.value[conditionIndex];
  if (!condition) return 0;
  let count = 0;
  condition.children.forEach((compare) => {
    compare.children?.forEach(() => count++);
  });
  return count;
};

/** 是否可以继续添加条件 */
const getAddEnable = (conditionIndex: number) => {
  return getConditionParamCount(conditionIndex) < GlobalConfig.shapeConditionMaxCount;
};

/** 更新条件/比较/参数的属性 */
const onUpdate = (
  conditionIndex: number,
  nodeId: string,
  data: Record<string, any>
) => {
  const list = cloneDeep(conditions.value);
  const condition = list[conditionIndex];
  if (!condition) return;

  const { logicalOperator, compareType, whenOperator, whenSourceKey, whenValue } = data;

  if (condition.id === nodeId) {
    if (logicalOperator) condition.logicalOperator = logicalOperator;
  }

  const compare = condition.children.find((c) => c.id === nodeId);
  if (compare) {
    if (logicalOperator) compare.logicalOperator = logicalOperator;
    if (compareType) compare.type = compareType;
  }

  const compareParam = condition.children
    .flatMap((c) => c.children ?? [])
    .find((p) => p.id === nodeId);
  if (compareParam) {
    if (whenOperator) compareParam.whenOperator = whenOperator;
    if (whenSourceKey !== undefined) compareParam.whenSourceKey = whenSourceKey;
    if (whenValue !== undefined) compareParam.whenValue = whenValue;
  }

  conditions.value = list;
};

/** 添加新的 compare 组 */
const onAddCompare = (conditionIndex: number) => {
  if (attrDisabled.value) return;
  if (!getAddEnable(conditionIndex)) {
    Modal.warning({
      title: '添加受限',
      content: '最大可添加' + GlobalConfig.shapeConditionMaxCount + '个条件，无法继续添加',
      okText: '知道了'
    });
    return;
  }
  const list = cloneDeep(conditions.value);
  const condition = list[conditionIndex];
  if (!condition) return;

  condition.children.push({
    id: generateID(),
    flag: 'compare',
    type: 'when',
    logicalOperator: 'and',
    children: [
      {
        id: generateID(),
        flag: 'compare-param',
        whenSourceKey: 'timeHour',
        whenOperator: 'MoreThanOrEqual',
        whenValue: 9
      }
    ]
  });

  conditions.value = list;
};

/** 在指定 compare 下添加 compare-param */
const onAddParam = (conditionIndex: number, compareId: string) => {
  if (attrDisabled.value) return;
  if (!getAddEnable(conditionIndex)) {
    Modal.warning({
      title: '添加受限',
      content: '最大可添加' + GlobalConfig.shapeConditionMaxCount + '个条件，无法继续添加',
      okText: '知道了'
    });
    return;
  }
  const list = cloneDeep(conditions.value);
  const condition = list[conditionIndex];
  if (!condition) return;

  const compare = condition.children.find((c) => c.id === compareId);
  if (compare) {
    compare.children = compare.children ?? [];
    compare.children.push({
      id: generateID(),
      flag: 'compare-param',
      whenSourceKey: 'timeHour',
      whenOperator: 'MoreThanOrEqual',
      whenValue: 9
    });
  }

  conditions.value = list;
};

/** 删除条件参数 */
const onDelete = (conditionIndex: number, paramId: string) => {
  const list = cloneDeep(conditions.value);
  const condition = list[conditionIndex];
  if (!condition) return;

  if (getConditionParamCount(conditionIndex) <= 1) {
    Modal.warning({
      title: '无法删除',
      content: '至少需要一个条件',
      okText: '知道了'
    });
    return;
  }

  const compare = condition.children.find((c) =>
    c.children?.some((p) => p.id === paramId)
  );
  if (!compare) return;

  compare.children = compare.children?.filter((p) => p.id !== paramId);

  if (!compare.children?.length) {
    condition.children = condition.children.filter((c) => c.id !== compare.id);
  }

  conditions.value = list;
};

// ========== 动画过程 ==========

const { animationTypeOptions } = useIfttt();

// 动画数据存储在 shape.attrs.widgetAnimations 中
const animations = computed<IAnimation[]>({
  get() {
    return props.shape.attrs.widgetAnimations ?? [];
  },
  set(val) {
    props.shape.setAttrs({ widgetAnimations: val });
  }
});

// 构造虚拟 IFTTT 对象，供 IFTTTAnimationItem 使用（它需要 ifttt.animations 来判断已用类型）
const virtualIfttt = computed<IFTTT>(() => ({
  id: props.shape.id + '_widgetIfttt',
  shapeId: props.shape.id,
  name: '组动效',
  condition: { id: '', children: [] } as Condition,
  animations: animations.value,
  loop: 'none'
}));

const sortedAnimations = computed(() => {
  const items = [...animations.value];
  items.sort((a, b) => {
    const aIndex = animationTypeOptions.findIndex((item) => item.value === a.type);
    const bIndex = animationTypeOptions.findIndex((item) => item.value === b.type);
    return aIndex - bIndex;
  });
  return items;
});

function createDefaultAnimation(): IAnimation {
  const type = animationTypeOptions.find((item) => !item.disabled)?.value || 'show';
  return {
    id: generateID(),
    type,
    name: '效果1',
    duration: 300,
    delay: 0,
    easeType: 'Linear',
    translateX: 100,
    translateY: 100,
    alpha: 70,
    toAlpha: 100,
    scale: 50,
    rotation: 90
  };
}

const onAddAnimation = () => {
  if (animations.value.length >= 5) {
    Modal.warning({ title: '不可添加', content: '最大可添加5个动画，无法继续添加', okText: '知道了' });
    return;
  }
  const types = animationTypeOptions.filter(
    (item) => !animations.value.some((a) => a.type === item.value) && !item.disabled
  );
  if (!types.length) {
    Modal.warning({ title: '不可添加', content: '没有更多可用的动画，无法继续添加', okText: '知道了' });
    return;
  }
  const list = cloneDeep(animations.value);
  const names = list.map((item) => item.name);
  let nameIndex = list.length + 1;
  let name = '效果' + nameIndex;
  while (names.includes(name)) { nameIndex++; name = '效果' + nameIndex; }

  list.push({
    id: generateID(),
    type: types[0].value,
    name,
    duration: 300,
    delay: 0,
    easeType: 'Linear',
    translateX: 100,
    translateY: 100,
    alpha: 70,
    toAlpha: 100,
    scale: 50,
    rotation: 90
  });
  animations.value = list;
};

const onDelAnimation = (aniId: string) => {
  if (animations.value.length <= 1) {
    Modal.warning({ title: '无法删除', content: '至少需要一个动画', okText: '知道了' });
    return;
  }
  animations.value = animations.value.filter((item) => item.id !== aniId);
};

const onUpdateAnimation = (aniId: string, data: Partial<Omit<IAnimation, 'id'>>) => {
  const list = cloneDeep(animations.value);
  const ani = list.find((item) => item.id === aniId);
  if (!ani) return;
  Object.keys(data).forEach((key) => {
    const _key = key as keyof typeof data;
    const val = data[_key];
    if (['name', 'id'].includes(_key) && !val) return;
    (ani as any)[_key] = val;
  });
  animations.value = list;
};
</script>
